/**
 * АНИМАЦИЯ ПОЛЕТА ЛЕТАЮЩИХ ОТРЯДОВ: у отряда загораются золотые крылья, затем он
 * летит по прямой от центра ячейки к центру цели, размахивая ими, и складывает их
 * на посадке. План строит utils/effects/buildWingedFlightCast. Это только вид:
 * правила полета описаны в utils/movement/calculateFlyingMovementCells.
 */
export const WINGED_FLIGHT_TEXTURE_KEYS = {
  // Правое крыло в своих цветах (левое — оно же с отрицательным масштабом)
  WING: 'flight_wing',
  // Размытый силуэт того же крыла: белый, красится tint
  WING_GLOW: 'flight_wing_glow',
  // Перо, которое роняет отряд в полете: белое, красится tint
  FEATHER: 'flight_feather',
};

// Картинки собирает npm run generate:flight-wings
export const WINGED_FLIGHT_TEXTURE_PATHS = {
  [WINGED_FLIGHT_TEXTURE_KEYS.WING]: '/img/icons/flight_wing.png',
  [WINGED_FLIGHT_TEXTURE_KEYS.WING_GLOW]: '/img/icons/flight_wing_glow.png',
  [WINGED_FLIGHT_TEXTURE_KEYS.FEATHER]: '/img/icons/flight_feather.png',
};

export const WINGED_FLIGHT = {
  // Разгорание крыльев: отряд еще на месте
  igniteMs: 460,
  // Сам полет: растет с расстоянием, доля — от размера ячейки
  flight: {baseMs: 340, perHexSizeMs: 75, minMs: 460, maxMs: 920},
  // Посадка: после нее видны только гаснущие крылья и волна
  landMs: 560,

  body: {
    // Высота над тенью, доля размера ячейки
    liftFactor: 0.2,
    // Доля полета, с которой отряд начинает снижаться
    descendFromShare: 0.68,
    // Покачивание в такт взмахам, доля размера ячейки
    bobFactor: 0.035,
    // Приседание перед взлетом, доля размера ячейки
    crouchFactor: 0.04,
  },

  wing: {
    // Размер холста крыла (с запасом под свечение), доля размера ячейки
    sizeFactor: 1.32,
    // Плечо относительно центра отряда, доли размера ячейки: крылья растут из-за иконки
    shoulderXFactor: 0.14,
    shoulderYFactor: 0.02,
    // Точка крепления на холсте, доли: совпадает с плечом образца (см. generateFlightWings)
    origin: {x: 0.1518, y: 0.6512},
    // Повороты, радианы: 0 — как на образце, больше нуля — кончик ниже
    foldedAngle: 1.5,
    raisedAngle: -0.28,
    loweredAngle: 0.56,
    // Масштаб сложенного крыла
    foldedScale: 0.32,
    // Период взмаха и доля периода на удар вниз: он быстрее подъема
    flapPeriodMs: 300,
    flapDownShare: 0.38,
    // Укорочение размаха на ударе вниз: крыло уходит вглубь
    strokeShorten: 0.24,
    // Наклон крыльев в сторону полета, радианы
    bank: 0.16,
    // Отставание и яркость следов-повторов крыла
    ghosts: [{lagMs: 55, alpha: 0.42}, {lagMs: 110, alpha: 0.24}, {lagMs: 165, alpha: 0.12}],
    glowAlpha: 0.8,
    // Размах свечения в такт взмаху
    glowPulse: 0.3,
    // Вспышка пера на ударе вниз
    strokeFlashAlpha: 0.26,
    // Темно-золотая подложка: свет сложением на светлых подложках (песок, снег)
    // выгорает в белое, а с ней крыло читается золотым на любой земле
    underlayTint: 0x9a5200,
    underlayAlpha: 0.72,
  },

  halo: {sizeFactor: 2.5, alpha: 0.62},
  rays: {sizeFactor: 2.5, alpha: 0.36, turn: 1.5},

  ignite: {
    auraSizeFactor: 3,
    auraAlpha: 0.72,
    // Искры сходятся к плечам
    moteCount: 18,
    moteFromFactor: 1.5,
    moteSizeFactor: 0.2,
    moteLifeShare: 0.72,
    moteTurn: 1.7,
    runeRadiusFactor: 0.9,
    runeTurn: 1.4,
    runeAlpha: 0.9,
    ringFromFactor: 2,
    ringCount: 2,
    ringLifeShare: 0.68,
  },

  launch: {
    flashSizeFactor: 2.3,
    flashMs: 260,
    raysSizeFactor: 3,
    raysMs: 340,
    ringMs: 520,
    ringToFactor: 2.3,
    sparkCount: 16,
    sparkReachFactor: 1.15,
    sparkSizeFactor: 0.2,
    sparkLifeMs: 480,
  },

  trail: {
    // Хвост — там, где отряд был столько времени назад
    lagMs: 230,
    coreThicknessFactor: 0.3,
    wideThicknessFactor: 0.85,
    // Подложка следа: так же, как у крыльев
    underlayTint: 0xb06400,
    underlayAlpha: 0.5,
    // След от кончиков крыльев
    tipLagMs: 170,
    tipThicknessFactor: 0.14,
    // Отступ следа кончика вбок от линии полета: при полете по вертикали
    // крылья расходятся шире всего
    tipOffsetFactor: 0.28,
    tipOffsetVerticalFactor: 0.52,
    alpha: 0.9,
    tailMs: 300,
  },

  sparks: {
    perHexSize: 7,
    minCount: 14,
    maxCount: 44,
    spreadFactor: 0.42,
    sizeFactor: 0.15,
    lifeMs: 560,
    fallFactor: 0.55,
  },

  feathers: {
    perHexSize: 1.4,
    minCount: 4,
    maxCount: 11,
    sizeFactor: 0.2,
    lifeMs: 880,
    spreadFactor: 0.62,
    fallFactor: 0.72,
    swayFactor: 0.14,
    alpha: 0.95,
  },

  land: {
    flashSizeFactor: 2.5,
    flashMs: 300,
    raysSizeFactor: 3.1,
    raysMs: 460,
    auraSizeFactor: 3.1,
    auraAlpha: 0.8,
    // Волны расходятся по ячейке друг за другом
    ringDelaysMs: [0, 110],
    ringMs: 520,
    ringToFactor: 2.6,
    sparkCount: 22,
    sparkReachFactor: 1.35,
    sparkSizeFactor: 0.22,
    sparkLifeMs: 560,
    // Сложенные крылья рассыпаются на искры
    wingFadeMs: 440,
  },
};
